/**
 * Per-message reasoning effort.
 *
 * These are OpenRouter's own `reasoning.effort` values, sent through to the
 * provider as-is. Until the OpenRouter switch "effort" was a private three-way
 * label that secretly picked a *model* (low/medium/high → gpt-5.2/5.4/5.5),
 * because the OpenAI Responses path had no effort parameter at all. The model is
 * now the user's own choice, so effort finally means effort.
 *
 * Persisted so the choice survives a refresh — it is a working preference, not a
 * per-message decision most users want to re-make every time.
 *
 * The server is the authority: it re-validates the value against the chosen
 * model's supported list and against the plan's ceiling (`maxEffort`, see
 * Subscription.normalizeSelection). That ceiling used to be duplicated here as
 * FREE_PLAN_EFFORT; it now arrives with the model list, so there is nothing left
 * to drift.
 *
 * **'none' is one of these, shown as "Disabled".** It is not a separate switch.
 * The composer had a "Thinking" tick box beside this control for a while, and the
 * two disagreed: `effort` is a field INSIDE OpenRouter's `reasoning` object and
 * `none` is one of its legal values, so the tick box being off silently threw the
 * chosen effort away. One dial, seven positions.
 *
 * WHICH of these a given model offers is not decided here either. It is a
 * per-model fact read from `tbl_model.reasoning_efforts`, arriving per model on
 * the picker response, because it genuinely varies: gpt-5.2/5.4/5.5 take
 * none/low/medium/high/xhigh, the gpt-5.6 series adds max, gpt-5-nano takes
 * minimal/low/medium/high and cannot be turned off at all, and across
 * OpenRouter's catalogue there are 22 distinct sets. This file only holds the
 * labels and the ordering.
 *
 * Which model's list applies is `chatSelection.activeModel` — the model that will
 * ANSWER, which with the picker off is the server's pinned default rather than
 * anything the client chose.
 */
export type ChatEffort = 'none' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max'

/**
 * Cheapest first. Mirrors ChatModels.ALL_EFFORTS exactly, including the order —
 * which is load-bearing, not cosmetic: `isEffortAbove` compares by index to
 * decide what a plan's ceiling locks out.
 */
export const CHAT_EFFORTS: ChatEffort[] = [
  'none',
  'minimal',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
]

/** Matches ChatModels.NO_THINKING_EFFORT. */
export const EFFORT_NONE: ChatEffort = 'none'

/**
 * Only a fallback for display before the server answers; it sends the real one.
 *
 * Kept equal to ChatModels.DEFAULT_CHAT_EFFORT so the control does not visibly
 * jump one tier the moment /ai/bot/models lands.
 */
export const DEFAULT_EFFORT: ChatEffort = 'medium'

export const EFFORT_LABELS: Record<ChatEffort, string> = {
  none: 'Disabled',
  minimal: 'Minimal',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra high',
  max: 'Max',
}

/**
 * The trade-off, spelled out per option. Cost is the thing users cannot see for
 * themselves, so it is what these say.
 */
export const EFFORT_DESCRIPTIONS: Record<ChatEffort, string> = {
  none: 'No reasoning. Fastest and cheapest',
  minimal: 'Barely thinks. Fast and cheap',
  low: 'Quick answers, light on your allowance',
  medium: 'Balanced depth and cost',
  high: 'Works through the problem properly',
  xhigh: 'Deeper still, noticeably slower',
  max: 'Everything it has. Use it sparingly',
}

export function isEffort(value: unknown): value is ChatEffort {
  return CHAT_EFFORTS.includes(value as ChatEffort)
}

/**
 * Does `value` ask for more reasoning than `ceiling` allows?
 *
 * The lock rule, in one place so the client and the server agree on what
 * "bigger" means — both compare position in the same cheapest-first order.
 * A null ceiling means no limit.
 *
 * Note this locks only what costs MORE. Nothing should ever stop someone
 * choosing an option that costs us less than the one they are entitled to — a
 * plan capped at Medium can still pick Disabled, Minimal or Low.
 */
export function isEffortAbove(value: string, ceiling: string | null): boolean {
  if (!ceiling) return false
  const at = CHAT_EFFORTS.indexOf(value as ChatEffort)
  const limit = CHAT_EFFORTS.indexOf(ceiling as ChatEffort)
  // An unknown value is not provably above the ceiling; leave it to the server,
  // which re-validates everything anyway.
  if (at === -1 || limit === -1) return false
  return at > limit
}

/**
 * Label for an effort the server sent.
 *
 * Falls back to the capitalised value rather than rendering `undefined`. The
 * effort list is data now — it comes from `tbl_model` and ultimately from
 * OpenRouter — so a value this file has no label for is a thing that can happen
 * without anyone changing this file. An unlovely "Xhigh" beats a blank row.
 */
export function effortLabel(value: string): string {
  return EFFORT_LABELS[value as ChatEffort] ?? value.charAt(0).toUpperCase() + value.slice(1)
}

/** Description for an effort the server sent, or '' when we have nothing to say. */
export function effortDescription(value: string): string {
  return EFFORT_DESCRIPTIONS[value as ChatEffort] ?? ''
}
